import React, { isValidElement } from "react";
import { Transformation as PdfTransformation } from "../../renderPrimitives/TransformationContext";
import { ListSchema, RowDefinition } from "../types/ListSchema";
import { measureTextForPDF } from "../../../modelViews/GraficElementUtils";
import { isUiModeDeveloper } from "../../../uiMode";
import { pageSizeA3, pageSizeA4 } from "../../../models/PageTypes";
import Size from "../../../models/Size";
import { PdfListRow } from "../types/ListJob";

export default class PdfListUtils {
  //
  // for 72 dpi for pdf
  //
  static mmToPdfPoint(mm: number): number {
    return (mm * 72) / 25.4;
  }

  static PdfPointToMM(pdfPoint: number): number {
    return (pdfPoint * 25.4) / 72;
  }

  // --------------------- Utilities ---------------------
  static mm(n: number) {
    return (n * 72) / 25.4;
  } // mm -> pt

  static pointToCssPixel(pt: number): number {
    return (pt * 72) / 25.4;
  }

  static cssPixelToPdfPoint(px: number) {
    return (px * 72) / 96; // = px * 0.75
  }

  static pdfPointToCssPixel(px: number) {
    return (px / 72) * 96;
  }

  //
  // Konvertiert CSS-Werte (mit Einheiten) zu PDF-Points (Zahlen)
  //

  static cmToNumber(value: undefined | number | string): number | undefined {
    if (!value) return undefined;
    const v = Number(value);
    const convertedValue = PdfListUtils.convertToPDFPoints(value + "cm");
    return convertedValue;
  }

  static convertToPDFPoints(value: number | string | undefined): number | undefined {
    if (value === undefined || value === null) return undefined;
    if (typeof value !== "string") return value; // Bereits eine Zahl in Points

    const stringValue = value.trim().toLowerCase();
    const numericValue = parseFloat(stringValue);

    if (isNaN(numericValue)) return undefined;

    //
    // Konvertierung zu Points für React-pdf (1 Point = 1/72 Inch)
    //
    if (stringValue.endsWith("px")) {
      return PdfListUtils.cssPixelToPdfPoint(numericValue); // 1px == 1pt
    } else if (stringValue.endsWith("cm")) {
      return PdfListUtils.mmToPdfPoint(numericValue * 10);
    } else if (stringValue.endsWith("mm")) {
      return PdfListUtils.mmToPdfPoint(numericValue);
    } else if (stringValue.endsWith("in")) {
      return numericValue * 72; // 1in = 72pt
    } else if (stringValue.endsWith("pt")) {
      console.log("WARNING: convertToPDFPoints pt:", numericValue);
      return numericValue; // Bereits in Points
    } else if (stringValue.endsWith("rem") || stringValue.endsWith("em")) {
      console.log("WARNING: convertToPDFPoints rem:", numericValue);
      return numericValue * 12; // Annahme: 1em/rem ≈ 12pt
    } else {
      // Keine Einheit - behandle als Points
      return numericValue;
    }
  }

  //
  // Konvertiert alle Properties mit Einheiten in einem Style-Objekt
  //
  static convertStyleUnitsForPDF(pdfStyle: any, debugPrefix: string = "PDF"): void {
    Object.keys(pdfStyle).forEach((prop) => {
      if (prop.toLowerCase().indexOf("color") < 0) {
        const value = pdfStyle[prop];
        if (typeof value === "string" && value.match(/^[-+]?\d*\.?\d+(px|cm|mm|in|pt|rem|em)$/i)) {
          const convertedValue = PdfListUtils.convertToPDFPoints(value);
          if (convertedValue !== undefined) {
            pdfStyle[prop] = convertedValue;
            // console.log(`${debugPrefix}: Konvertiert ${prop}: "${value}" → ${convertedValue}pt`);
          } else {
            // Wenn Konvertierung fehlschlägt, Property entfernen und warnen
            delete pdfStyle[prop];
            console.error(`${debugPrefix}: Konnte ${prop}: "${value}" nicht konvertieren - Property entfernt`);
          }
        }
      }
    });

    // Debug-Ausgabe für problematische Properties (nur in Development)
    if (isUiModeDeveloper()) {
      const problematicKeys = ["width", "height", "minWidth", "maxWidth", "minHeight", "maxHeight"];
      for (const key of problematicKeys) {
        const value = pdfStyle[key];
        if (value !== undefined && typeof value === "string" && value.includes("undefined")) {
          console.error(
            `RImg: Problematischer ${key} Wert:`,
            value,
            "Original:",
            pdfStyle[key as keyof typeof pdfStyle],
          );
        }
      }
    }
  }

  // Style-Properties skalieren und verschieben
  static applyTransformation(styleObj: any, transformation: PdfTransformation | undefined) {
    if (!transformation) return;

    let { scale, translate } = transformation;

    //
    // pixel to point conversion
    // 1px ≈ 0.75pt
    //

    translate = { x: translate.x * 0.75, y: translate.y * 0.75 };

    console.log("Pdf: applyTransformation", scale, translate, styleObj);

    const keysToScale = [
      "left", // no itx
      "top", // no itx
      "width", // no itx
      "height", // no itx
      "minWidth", // no itx
      "maxWidth", // no itx
      "maxHeight", // no itx
      "minHeight", // no itx
      "padding", // no itx
      "paddingLeft", // no itx
      "paddingRight", // no itx
      "paddingTop", // no itx
      "paddingBottom", // no itx
      "margin", // no itx
      "marginLeft", // no itx
      "marginRight", // no itx
      "marginTop", // no itx
      "marginBottom", // no itx
      "fontSize", // no itx
    ];

    for (const key of keysToScale) {
      const value = styleObj[key];
      console.log(`Pdf: applyTransformation: ${key} =`, value, "scale =", scale);
      if (value) {
        if (typeof value === "number") {
          styleObj[key] *= scale;
        } else {
          if (typeof value === "string" && !value.endsWith("%")) {
            console.error(`Pdf: applyTransformation: Skipping non-numeric style property ${key}`, value);
          }
        }
      }
    }

    //
    // Nur left, top, right, bottom verschieben
    //
    if (typeof styleObj.left === "number") styleObj.left += translate.x;
    if (typeof styleObj.right === "number") styleObj.right += translate.x;
    if (typeof styleObj.top === "number") styleObj.top += translate.y;
    if (typeof styleObj.bottom === "number") styleObj.bottom += translate.y;
  }

  static splitRowsToPages(arr: any[], size: number): any[][] {
    if (size <= 0) return [arr];

    const out: any[][] = [];

    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  static generatePageBreak(
    schema: ListSchema,
    usableHeight: number,
    rows: Array<PdfListRow>,
    isTableOfContents: boolean,
  ): any[][] {
    const out: any[][] = [];

    let currentPageRows: any[] = [];

    let currentSumOfHeights = 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      row.heightMM = PdfListUtils.getRowHeightInMM(schema, row);

      let topLevelPageBreak = false;
      if (!isTableOfContents && schema.onePagePerTopLevelRow && row?.level === 1) {
        if (i > 0) {
          topLevelPageBreak = true;
        }
        //
        // Die Zeile wird als Überschrift im Header verwendt und kostet somit keinen Platz im Body der Seite
        //
        row.heightMM = 0;
      }

      currentSumOfHeights += row.heightMM || 0;
      if (currentSumOfHeights > usableHeight || topLevelPageBreak) {
        if (currentPageRows.length > 0) {
          out.push(currentPageRows);
        }

        currentPageRows = [];
        currentSumOfHeights = row.heightMM || 0;
      }

      currentPageRows.push(row);
    }

    if (currentPageRows.length > 0) {
      out.push(currentPageRows);
    }

    return out;
  }

  //
  // Funktion zur Berechnung der tatsächlichen maximalen Spaltenbreite
  //
  static calculateMaxColumnWidths(schema: ListSchema, data: any[], isTableOfContents: boolean) {
    const fontSizeInPx = schema.pageSpec.fontSizeInPx; // Entspricht dem fontSize in CellText

    const maxLevel = Math.max(...data.map((row) => row.level || 1));

    for (let levelIndex = 1; levelIndex <= maxLevel; levelIndex++) {
      const rowDefinitions = PdfListUtils.getDefinitionsForLevel(
        schema,
        levelIndex,
        isTableOfContents ? "toc" : undefined, // no itx
      );

      rowDefinitions.forEach((rowDefinition) => {
        const columns = rowDefinition.columns;

        for (const column of columns) {
          const key = column.key;
          let maxWidth = 0;

          if (column.widthInMM) {
            // Wenn eine feste Breite definiert ist, diese verwenden
            maxWidth = PdfListUtils.mmToPdfPoint(column.widthInMM);
            data.forEach((row) => {
              if (row.level == levelIndex) {
                column.maxContentWidth = maxWidth;
              }
            });
          } else {
            data.forEach((row) => {
              if (row.level == levelIndex) {
                const cellValue = column.format ? column.format(row[column.key], row) : String(row[column.key] ?? "");

                // Verwende PDFKit-Messung (gleiche Metriken wie React-PDF)
                const cellWidth = measureTextForPDF(cellValue, fontSizeInPx + "px").width;

                maxWidth = Math.max(maxWidth, cellWidth);
              }

              column.maxContentWidth = maxWidth + 2 * schema.pageSpec.horizontalCellPaddingInPx;
            });
          }
        }
      });
    }
  }

  static getDefinitionForRow(schema: ListSchema, row: any): RowDefinition | undefined {
    if (!row) return undefined;

    const level = row.level || 1;
    const type = row.type; // Default type if not specified

    let res = schema.rowDefinitions.find((rd) => rd.level === level && rd.type === type);
    if (!res && type) {
      res = schema.rowDefinitions.find((rd) => rd.level === level && rd.type === "default");
    }

    return res;
  }

  static getDefinitionsForLevel(schema: ListSchema, level: number, type?: string): RowDefinition[] {
    return schema.rowDefinitions.filter((rd) => rd.level === level && (type ? rd.type === type : true));
  }

  static getRowHeightInMM(schema: ListSchema, row: PdfListRow): number {
    if (row.type === "empty") {
      return (row.heightMM || schema.pageSpec.rowHeightInMM) / 2;
    }

    const rowDefinition = PdfListUtils.getDefinitionForRow(schema, row);
    if (!rowDefinition) return 0;

    //
    // Calculate height based on row definition and content
    //

    let heightInMM = row.heightMM || schema.pageSpec.rowHeightInMM;

    rowDefinition.columns.forEach((col) => {
      if (col.measure) {
        const res = col.measure(schema, col, row);
        heightInMM = Math.max(heightInMM, res.heightInMM);
      } else if (col.heightInMM) {
        heightInMM = Math.max(heightInMM, col.heightInMM);
      } else {
        // do nothing
      }
    });

    return heightInMM;
  }

  static getPageSizeInCM(schema: ListSchema): Size {
    const isLandscape = schema.pageSpec.orientation === "landscape";
    const xSize: Size = schema.pageSpec.size == "A4" ? pageSizeA4 : pageSizeA3;

    return isLandscape ? new Size(xSize.height, xSize.width) : xSize;
  }

  static useablePageSizeInMM(schema: ListSchema) {
    const pageSizeInCM = PdfListUtils.getPageSizeInCM(schema);

    const pageSpec = schema.pageSpec;

    let { frameMargin, footerHeightInCM, headerRowHeightInCM: headerRowHeight } = pageSpec;
    frameMargin = { ...frameMargin };

    //
    // convert to mm
    //
    frameMargin.Top *= 10;
    frameMargin.Right *= 10;
    frameMargin.Bottom *= 10;
    frameMargin.Left *= 10;

    // Fallbacks für undefined Werte

    let headerHeight = pageSpec.headerLayout.heightInCM * 10;
    let footerHeight = footerHeightInCM * 10;

    headerRowHeight = headerRowHeight || 20;

    if (schema.pageSpec.noHeader) {
      headerHeight = 0;
    }
    if (schema.pageSpec.noFooter) {
      footerHeight = 0;
    }

    const usableLeft = frameMargin.Left || 10;
    const usableRight = frameMargin.Right || 10;

    const usableTop = (frameMargin.Top || 10) + headerHeight;
    const usableBottom = (frameMargin.Bottom || 10) + footerHeight;

    const usableWidth = pageSizeInCM.width * 10 - usableLeft - usableRight; // A4 Fallback
    const usableHeight = pageSizeInCM.height * 10 - usableTop - usableBottom; // A4 Fallback

    return {
      usableWidth,
      usableHeight,
      usableLeft,
      usableTop,
      usableRight,
      usableBottom,
    };
  }

  // static dumpTree(node: React.ReactNode, depth = 0) {
  //     React.Children.forEach(node, child => {
  //         if (!isValidElement(child)) return;
  //         const type =
  //             typeof child.type === "string"
  //                 ? child.type
  //                 : (child.type as any).name || "[Anon]";
  //         const pad = "  ".repeat(depth);
  //         console.log(
  //             `${pad}<${type}>`,
  //             child.props?.style ? { style: child.props.style } : ""
  //         );

  //         const children = child.props.children || child.props.pageElement?.props?.children;

  //         if (children) {
  //             React.Children.forEach(children, child => {
  //                 PdfListUtils.dumpTree(child, depth + 1);
  //             });
  //         }
  //     });
  // }
}
